import { useParams, useNavigate } from "react-router-dom";
import productData from "../../assets/localdb/productdata";
import useProducts from "../../hooks/useProducts";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useUser } from "../../hooks/useUser";
import SafeImage from "../../components/common/SafeImage";

// Helper function to get full image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // If it's already a full URL, return as is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // If it's a relative path, prefix with base URL
  if (imagePath.startsWith("/")) {
    return (
      (import.meta.env.VITE_API_URL || "http://localhost:8000") + imagePath
    );
  }

  // If it doesn't start with /, add it
  return (import.meta.env.VITE_API_URL || "http://localhost:8000") + imagePath;
};

export default function Details() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { getProductsByid } = useProducts();
  const { data: productResult, isLoading: Loadingproduct } =
    getProductsByid(id);

  if (!Loadingproduct && !productResult.data) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <div className="alert alert-danger">
                  <h4>Product Not Found</h4>
                  <p>The product you're looking for doesn't exist.</p>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => navigate("/products")}
                  >
                    Back to Products
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return Loadingproduct ? (
    <LoadingSpinner />
  ) : (
    <div className="container py-4">
      <h2 className="mb-4 text-primary">Product Details</h2>

      <div className="row g-4">
        {/* Image Card */}
        <div className="col-md-4">
          <div className="card text-center shadow-sm rounded-4">
            <SafeImage
              src={(() => {
                try {
                  return JSON.parse(
                    productResult?.data?.product_image || [],
                  )?.[0];
                } catch (e) {
                  return productResult?.data?.product_image;
                }
              })()}
              className="card-img-top rounded-top-4"
              alt={productResult.data.product_name}
              style={{ maxHeight: "250px", objectFit: "cover", margin: 10 }}
            />
            <div className="card-body">
              <h5 className="card-title mb-0">
                {productResult.data.product_name}
              </h5>

              <div className="d-flex justify-content-center gap-3 mt-3">
                <button
                  onClick={() => navigate(`/adminproductsaddorupdate/${id}`)}
                  className="btn btn-sm btn-outline-success"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (user?.role === "vendor") {
                      navigate("/product");
                    } else {
                      navigate("/listofproducts");
                    }
                  }}
                  className="btn btn-sm btn-outline-danger"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="col-md-8">
          <div className="card shadow-sm rounded-4">
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Product ID
                </div>
                <div className="col-sm-8">#{productResult.data.product_id}</div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Product Name
                </div>
                <div className="col-sm-8">
                  {productResult.data.product_name}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Category ID
                </div>
                <div className="col-sm-8">{productResult.data.category_id}</div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">Category</div>
                <div className="col-sm-8">
                  {productResult.data.category?.category_name}
                  {productResult.data.category?.category_image && (
                    <div className="mt-1">
                      <SafeImage
                        src={getImageUrl(
                          productResult.data.category.category_image,
                        )}
                        alt="Category"
                        style={{ width: 30, height: 30, objectFit: "cover" }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Subcategory ID
                </div>
                <div className="col-sm-8">
                  {productResult.data.subcategory_id}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Subcategory
                </div>
                <div className="col-sm-8">
                  {productResult.data.subcategory?.subcategory_name}
                  {productResult.data.subcategory?.subcategory_image && (
                    <div className="mt-1">
                      <SafeImage
                        src={getImageUrl(
                          productResult.data.subcategory.subcategory_image,
                        )}
                        alt="Subcategory"
                        style={{ width: 30, height: 30, objectFit: "cover" }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Description
                </div>
                <div className="col-sm-8">
                  <div
                    style={{ paddingLeft: 0, margin: 0 }}
                    dangerouslySetInnerHTML={{
                      __html: productResult.data.description,
                    }}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">Status</div>
                <div className="col-sm-8">
                  {productResult.data.status === "active" ? (
                    <span className="badge bg-success text-white">Active</span>
                  ) : (
                    <span className="badge bg-danger text-white">Inactive</span>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Age Validation Required
                </div>
                <div className="col-sm-8">
                  {productResult.data.age_validation_flag ? (
                    <span className="badge bg-warning text-dark">Required</span>
                  ) : (
                    <span className="badge bg-secondary text-white">
                      Not Required
                    </span>
                  )}
                </div>
              </div>

              {/* Product Variants Section */}
              {Array.isArray(productResult.data.product_variables) &&
                productResult.data.product_variables.length > 0 && (
                  <div className="row mb-3">
                    <div className="col-sm-4 fw-semibold text-muted">
                      Product Variants
                    </div>
                    <div className="col-sm-8">
                      <table className="table table-bordered table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Variant Name</th>
                            <th>SKU</th>
                            <th>Image</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productResult.data.product_variables.map(
                            (variant, idx) => (
                              <tr key={variant.sku_id || idx}>
                                <td>{variant.variable_name}</td>
                                <td>{variant.sku_id}</td>
                                <td>
                                  {variant.image_url ? (
                                    <SafeImage
                                      src={getImageUrl(variant.image_url)}
                                      alt={variant.variable_name}
                                      style={{
                                        width: 40,
                                        height: 40,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                      }}
                                    />
                                  ) : (
                                    <span className="text-muted">No Image</span>
                                  )}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              {productResult.data.product_variables &&
                productResult.data.product_variables.length === 0 && (
                  <div className="row mb-3">
                    <div className="col-sm-4 fw-semibold text-muted">
                      Product Variants
                    </div>
                    <div className="col-sm-8">
                      <span className="text-muted">No variants available</span>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
